import React, { createContext, useContext, useEffect, useState } from 'react'
import { get } from 'helpers/api_helper'

const Context = createContext(null)
const contracts = {
  players: ['/players/reputations', 'userIds', 'userId'],
  events: ['/events/review-summaries', 'eventIds', 'eventId'],
  organizers: ['/organizers/review-summaries', 'organizerIds', 'organizerId']
}
const validId = (kind, id) => kind === 'players'
  ? /^[1-9]\d*$/.test(String(id)) && Number.isSafeInteger(Number(id)) : /^[a-f\d]{24}$/i.test(String(id))

// PRD 16 LR5 (register E25): a Tenis10 child's slot (profileId) carries the parent's userId — a child has no reputation,
// and the parent's is never shown on the child's slot.
export const identifiedPlayer = player => validId('players', player?.userId) && !player?.profileId
  && !['BYE', 'PENDING', 'TBD'].includes(player?.type)
// A Tenis10 child's entry keeps the parent's snapshot on `user` and the child on the entry's own `profileId` and `profile` (Events):
// the person of that entry is the child — the same rule as a slot: its name, its profileId and the parent's userId (the payment key),
// never the parent's account, place, level or plan; the parent is only its contact (PRD 16 LR11b, register E39 (1)). Its service fee is the
// entry's own amount (the parent's snapshot carries none). Its W rides on the snapshot, computed for the child (PRD 16 LR11c).
// RELEASE-TENIS10 § 11 P78: its own number — `profile.childId` on an entry, `childId` flat on a Lucky Draw slot.
// P73 (Tudor 2026-10-29): where the child lives (`place`) — the place of its Accounts hit (`entry.child`, the admin read decorates it),
// never the parent's snapshot place.
const CONTACT_FIELDS = ['id', 'userId', 'firstName', 'lastName', 'email', 'phone']
const childPerson = entry => {
  const contact = Object.fromEntries(CONTACT_FIELDS.filter(field => entry.user[field] !== undefined).map(field => [field, entry.user[field]]))
  const childId = entry.profile?.childId ?? entry.childId
  return { userId: entry.user.userId ?? entry.userId, profileId: entry.profileId, firstName: entry.profile?.firstName,
    lastName: entry.profile?.lastName, profilePicture: entry.profile?.profilePicture, registrationFee: entry.amount ?? entry.user.registrationFee,
    ...(childId != null && { childId }), ...(entry.user.w === true && { w: true }), ...(entry.child?.location && { place: entry.child.location }),
    // P41: a child not claimed yet — its entry is held by its tenis10.ro family, the contact is that parent (name, phone)
    ...(entry.holder?.kind === 'migrationFamily' && { unclaimed: true }), contact }
}
export const entryUser = entry => entry?.user && entry.profileId ? childPerson(entry) : entry?.user
export function PlayerRatingScope({ players = [], children }) {
  return <PublicRatingScope kind="players" ids={players.filter(identifiedPlayer).map(player => player.userId)}>{children}</PublicRatingScope>
}

// One request per visible batch, never one request per identity. Values are
// authoritative public summaries; the browser does not aggregate reviews.
export function PublicRatingScope({ kind, ids = [], children }) {
  const key = JSON.stringify([kind, [...new Set(ids.filter(id => validId(kind, id)).map(String))].sort()])
  const [state, setState] = useState(null)
  useEffect(() => {
    let disposed = false
    let generation = 0
    const [scope, selected] = JSON.parse(key)
    const [endpoint, param, field] = contracts[scope]
    const refresh = async () => {
      const request = ++generation
      setState({ key, status: 'loading', rows: {} })
      try {
        const batches = []
        for (let start = 0; start < selected.length; start += 100) {
          batches.push(get(`${endpoint}?${new URLSearchParams({ [param]: selected.slice(start, start + 100).join(',') })}`))
        }
        const results = await Promise.all(batches)
        const rows = {}
        for (const result of results) {
          if (!Array.isArray(result?.data)) throw Error('Invalid public summary')
          for (const row of result.data) {
            const id = String(row[field])
            if (selected.includes(id)) (rows[id] || (rows[id] = [])).push(row)
          }
        }
        if (!disposed && request === generation) setState({ key, status: 'ready', rows })
      } catch {
        if (!disposed && request === generation) setState({ key, status: 'error', rows: {} })
      }
    }
    refresh()
    const onVisible = () => { if (document.visibilityState === 'visible') refresh() }
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', onVisible)
    return () => { disposed = true; window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', onVisible) }
  }, [key])
  const value = state?.key === key ? { ...state, kind } : { kind, status: 'loading', rows: {} }
  return <Context.Provider value={value}>{children}</Context.Provider>
}
export function usePublicRating(kind, id) {
  const scope = useContext(Context)
  return scope?.kind === kind ? { status: scope.status, rows: scope.rows[String(id)] || [] } : { status: 'ready', rows: [] }
}
